import { z } from "zod";

// Capture version tags a whole capture payload so future plan changes are
// distinguishable from historical rows without a migration.
export const SCRAPE_CAPTURE_VERSIONS = [
  // Fetch-stage capture persisted before analysis starts.
  "yeet.fetch-capture.v2",
  // Yeet page screenshot: the ScrapingBee shot staged at capture time.
  "yeet.fetch-screenshot.v2",
] as const;
export const SCRAPE_CAPTURE_VERSION = {
  YEET_FETCH_CAPTURE_V2: "yeet.fetch-capture.v2",
  YEET_FETCH_SCREENSHOT_V2: "yeet.fetch-screenshot.v2",
} as const satisfies Record<string, ScrapeCaptureVersion>;
export const ScrapeCaptureVersionSchema = z.enum(SCRAPE_CAPTURE_VERSIONS);
export type ScrapeCaptureVersion = z.infer<typeof ScrapeCaptureVersionSchema>;

// One immutable blob kind per captured artifact. `screenshot_png` is the
// visual-evidence capture; the other four kinds are the required set every
// page capture must produce (see REQUIRED_CAPTURE_ARTIFACT_KINDS in api/crawl).
export const SCRAPE_ARTIFACT_KINDS = [
  "rendered_html",
  "source_html",
  "page_text",
  "provider_envelope",
  "screenshot_png",
] as const;
export const SCRAPE_ARTIFACT_KIND = {
  RENDERED_HTML: "rendered_html",
  SOURCE_HTML: "source_html",
  PAGE_TEXT: "page_text",
  PROVIDER_ENVELOPE: "provider_envelope",
  SCREENSHOT_PNG: "screenshot_png",
} as const satisfies Record<string, ScrapeArtifactKind>;
export const ScrapeArtifactKindSchema = z.enum(SCRAPE_ARTIFACT_KINDS);
export type ScrapeArtifactKind = z.infer<typeof ScrapeArtifactKindSchema>;

// Screenshot capture devices. Adding a device here fans out one extra
// resources-on ScrapingBee call per classic-crawled page and one extra
// harness shot per yeet page; both capture paths derive from this list.
export const SCREENSHOT_DEVICES = ["desktop", "mobile"] as const;
export const SCREENSHOT_DEVICE = {
  DESKTOP: "desktop",
  MOBILE: "mobile",
} as const satisfies Record<string, ScreenshotDevice>;
export const ScreenshotDeviceSchema = z.enum(SCREENSHOT_DEVICES);
export type ScreenshotDevice = z.infer<typeof ScreenshotDeviceSchema>;

export const ORIGIN_FILE_KINDS = ["robots", "llms"] as const;
export const ORIGIN_FILE_KIND = {
  ROBOTS: "robots",
  LLMS: "llms",
} as const satisfies Record<string, OriginFileKind>;
export const OriginFileKindSchema = z.enum(ORIGIN_FILE_KINDS);
export type OriginFileKind = z.infer<typeof OriginFileKindSchema>;

export const ORIGIN_FILE_STATES = ["pending", "resolved"] as const;
export const ORIGIN_FILE_STATE = {
  PENDING: "pending",
  RESOLVED: "resolved",
} as const satisfies Record<string, OriginFileState>;
export const OriginFileStateSchema = z.enum(ORIGIN_FILE_STATES);
export type OriginFileState = z.infer<typeof OriginFileStateSchema>;

// Which rendering of a captured page is being read: the browser-rendered DOM,
// or the bytes the origin served.
export const TEXT_REPRESENTATIONS = ["rendered", "source"] as const;
export const TextRepresentationSchema = z.enum(TEXT_REPRESENTATIONS);
export type TextRepresentation = z.infer<typeof TextRepresentationSchema>;
export const TEXT_REPRESENTATION = {
  RENDERED: "rendered",
  SOURCE: "source",
} as const satisfies Record<string, TextRepresentation>;

// Image formats a captured screenshot may be stored as.
export const SCREENSHOT_MIME_TYPES = ["image/jpeg", "image/png"] as const;
export const ScreenshotMimeTypeSchema = z.enum(SCREENSHOT_MIME_TYPES);
export type ScreenshotMimeType = z.infer<typeof ScreenshotMimeTypeSchema>;
export const SCREENSHOT_MIME_TYPE = {
  IMAGE_JPEG: "image/jpeg",
  IMAGE_PNG: "image/png",
} as const satisfies Record<string, ScreenshotMimeType>;

// ScrapingBee proxy tiers, cheapest first. A tier decides what a render costs
// and how likely a bot-defended origin is to serve it.
export const SCRAPE_PROXY_TIERS = ["premium", "stealth"] as const;
export const ScrapeProxyTierSchema = z.enum(SCRAPE_PROXY_TIERS);
export type ScrapeProxyTier = z.infer<typeof ScrapeProxyTierSchema>;
export const SCRAPE_PROXY_TIER = {
  PREMIUM: "premium",
  STEALTH: "stealth",
} as const satisfies Record<string, ScrapeProxyTier>;
